import React from 'react';
import { Box, Heading, Center, Text, useMedia } from '@gluestack-ui/themed';

import ScrollableScreenContainer from '../../../components/ScrollableScreenContainer';
import log from '../../../utils/logger';
import { usePreviewContent, usePreviewNavigation } from '../hooks/useModulePreviewStore';
import useModulePreviewData from '../hooks/useModulePreviewData';

// Import student components
import Greeting from '../../../../student/course/components/Greeting';
import SelectedModuleCard from '../../../../student/course/components/SelectedModuleCard';
import ModuleList from '../../../../student/course/components/ModuleList';

import { getColorForModule } from '../../../utils/getColorForModule';

/**
 * CoursePreview component
 * Adapts the student CourseScreen for use in preview mode
 */
const CoursePreview = () => {
  const { module, tasks, allModules } = usePreviewContent();
  const { setActiveView } = usePreviewNavigation();
  const media = useMedia();

  // Transform data for student components
  const previewData = useModulePreviewData(module, tasks, allModules);

  if (!module) {
    return (
      <Center flex={1}>
        <Text>No module data available for preview</Text>
      </Center>
    );
  }

  // Mock handlers for student components
  const handleWorkOnModule = () => {
    // Switch to module view
    setActiveView('module');
  };

  const handleOpenModule = ({ moduleId, courseId }) => {
    // No-op in preview mode
    log.info('Preview: Open module requested', { moduleId, courseId });
  };

  // Filter out the selected module from the modules list
  const otherModules = allModules.filter((m) => m.id !== module.id);

  return (
    <ScrollableScreenContainer px="$2" bg="$backgroundLight100">
      {/* User greeting component */}
      <Greeting user={previewData.user} showMenu={!media.md} handleLogout={() => {}} />

      {/* Current module section */}
      <Box justifyContent="space-between" $xl-flexDirection="row" mb={36}>
        <Heading size="md" mb={22}>
          Current Module
        </Heading>
        <SelectedModuleCard
          color={getColorForModule(module)}
          imageUrl={module.imageUrl}
          title={module.title}
          description={module.metadata?.description}
          levels={module.metadata.levels}
          lecturer={
            module.author
              ? `${module.author.title || ''} ${module.author.firstname || ''} ${
                  module.author.lastname || ''
                }`.trim() ||
                module.author.name ||
                ''
              : 'Preview Lecturer'
          }
          handleWorkOnModule={handleWorkOnModule}
        />
      </Box>

      {/* Other modules list - only shown if there are other modules */}
      {otherModules.length > 0 && <ModuleList modules={otherModules} handleOpenModule={handleOpenModule} />}
    </ScrollableScreenContainer>
  );
};

export default CoursePreview;
